import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, Copy, ChevronLeft, ChevronRight, FolderOpen, Image, Layout } from 'lucide-react';
import LinkSelector from './LinkSelector';
import TipTapEditor from './TipTapEditor';
import ButtonEnhancements from './ButtonEnhancements';

// Width options matching Hero block
const WIDTH_OPTIONS = [
  { value: 'full', label: 'Full Width', description: 'Edge to edge (100%)' },
  { value: 'container', label: 'Container', description: 'Matches page width' },
  { value: 'large', label: 'Large (75%)', description: 'Centered large block' },
  { value: 'medium', label: 'Medium (60%)', description: 'Centered medium block' },
  { value: 'small', label: 'Small (45%)', description: 'Compact layout' },
];

const ImageTextCarouselEditor = ({ content, onChange, openMediaPicker, blockId, pages = [], anchors = [] }) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  // Support for multiple slides (carousel mode)
  const slides = content.slides || [];
  
  // Create default slide from legacy content if no slides exist
  const createDefaultSlide = () => ({
    imageUrl: content.imageUrl || '',
    imageAlt: content.imageAlt || '',
    textHtml: content.textHtml || '<p>Text content</p>',
    imagePosition: content.imagePosition || 'left',
    imageSizeMode: content.imageSizeMode || 'width',
    imageWidth: content.imageWidth || 300,
    imageHeight: content.imageHeight || 200,
    imageAlign: content.imageAlign || 'center',
    shapeMask: content.shapeMask || 'none',
    shapeSize: content.shapeSize || 250,
    shapeBorderWidth: content.shapeBorderWidth || 0,
    shapeBorderColor: content.shapeBorderColor || '#ffffff',
    paddingSide: content.paddingSide || 'all',
    paddingSize: content.paddingSize || 0,
  });

  // Use slides if available, or create from legacy content
  const effectiveSlides = slides.length > 0 ? slides : [createDefaultSlide()];
  const currentSlide = effectiveSlides[activeSlideIndex] || effectiveSlides[0];

  const updateSlide = (index, field, value) => {
    const newSlides = [...effectiveSlides];
    newSlides[index] = { ...newSlides[index], [field]: value };
    onChange({ ...content, slides: newSlides, carouselMode: newSlides.length > 1 });
  };

  const addSlide = () => {
    const newSlide = createDefaultSlide();
    const newSlides = [...effectiveSlides, newSlide];
    onChange({ ...content, slides: newSlides, carouselMode: true });
    setActiveSlideIndex(newSlides.length - 1);
  };

  const duplicateSlide = (index) => {
    const newSlides = [...effectiveSlides];
    newSlides.splice(index + 1, 0, { ...effectiveSlides[index] });
    onChange({ ...content, slides: newSlides, carouselMode: true });
    setActiveSlideIndex(index + 1);
  };

  const removeSlide = (index) => {
    if (effectiveSlides.length <= 1) return;
    const newSlides = effectiveSlides.filter((_, i) => i !== index);
    onChange({ ...content, slides: newSlides, carouselMode: newSlides.length > 1 });
    setActiveSlideIndex(Math.min(activeSlideIndex, newSlides.length - 1));
  };

  const goToPrev = () => {
    setActiveSlideIndex(prev => prev > 0 ? prev - 1 : effectiveSlides.length - 1);
  };

  const goToNext = () => {
    setActiveSlideIndex(prev => (prev + 1) % effectiveSlides.length);
  };

  // Handle media picker callback
  const handleMediaSelect = (media) => {
    updateSlide(activeSlideIndex, 'imageUrl', media.url);
    // Also set alt text from media library if available
    if (media.alt_text) {
      updateSlide(activeSlideIndex, 'imageAlt', media.alt_text);
    }
  };

  // Shape mask options
  const shapeMaskOptions = [
    { value: 'none', label: 'No Mask' },
    { value: 'circle', label: 'Circle' },
    { value: 'ellipse', label: 'Ellipse' },
    { value: 'rounded', label: 'Rounded' },
    { value: 'diamond', label: 'Diamond' },
    { value: 'hexagon', label: 'Hexagon' },
    { value: 'octagon', label: 'Octagon' },
    { value: 'star', label: 'Star' },
    { value: 'arch', label: 'Arch' },
  ];

  return (
    <div className="space-y-4">
      {/* Slide Navigation */}
      <div className="flex items-center justify-between bg-slate-50 p-2 rounded-lg">
        <div className="flex items-center gap-2">
          {effectiveSlides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setActiveSlideIndex(idx)}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                idx === activeSlideIndex 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              {idx + 1}
            </button>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={addSlide}
            className="h-8 px-2"
          >
            <Plus size={14} className="mr-1" />
            Add Slide
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={goToPrev} className="h-8 w-8 p-0">
            <ChevronLeft size={16} />
          </Button>
          <span className="text-sm text-slate-500">
            Slide {activeSlideIndex + 1} of {effectiveSlides.length}
          </span>
          <Button variant="ghost" size="sm" onClick={goToNext} className="h-8 w-8 p-0">
            <ChevronRight size={16} />
          </Button>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => duplicateSlide(activeSlideIndex)}
            className="h-8 px-2"
            title="Duplicate slide"
          >
            <Copy size={14} />
          </Button>
          {effectiveSlides.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeSlide(activeSlideIndex)}
              className="h-8 px-2 text-red-500 hover:text-red-700"
              title="Delete slide"
            >
              <Trash2 size={14} />
            </Button>
          )}
        </div>
      </div>

      {/* Block Width Control */}
      <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-2">
          <Layout size={14} className="text-blue-500" />
          <span className="text-xs font-semibold text-slate-700">Block Width</span>
        </div>
        <div className="grid grid-cols-5 gap-2">
          {WIDTH_OPTIONS.map((w) => (
            <button
              key={w.value}
              onClick={() => onChange({ ...content, blockWidth: w.value })}
              className={`p-2 rounded-lg border text-xs font-medium transition-all ${
                (content.blockWidth || 'container') === w.value
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
              }`}
              title={w.description}
            >
              {w.label.split(' ')[0]}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-2">
          {WIDTH_OPTIONS.find(w => w.value === (content.blockWidth || 'container'))?.description}
        </p>
      </div>

      {/* Carousel Settings */}
      {effectiveSlides.length > 1 && (
        <div className="flex items-center gap-4 p-2 bg-blue-50 rounded-lg">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={content.autoPlay !== false}
              onChange={(e) => onChange({ ...content, autoPlay: e.target.checked })}
              className="rounded border-slate-300"
            />
            Auto-play
          </label>
          {content.autoPlay !== false && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">Speed:</label>
              <Input
                type="number"
                value={content.autoPlaySpeed || 5000}
                onChange={(e) => onChange({ ...content, autoPlaySpeed: parseInt(e.target.value) || 5000 })}
                className="w-20 h-8 text-xs"
                min="1000"
                max="15000"
                step="500"
              />
              <span className="text-xs text-slate-400">ms</span>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={content.showDots !== false}
              onChange={(e) => onChange({ ...content, showDots: e.target.checked })}
              className="rounded border-slate-300"
            />
            Show dots
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={content.showArrows !== false}
              onChange={(e) => onChange({ ...content, showArrows: e.target.checked })}
              className="rounded border-slate-300"
            />
            Show arrows
          </label>
        </div>
      )}

      {/* Current Slide Editor */}
      <div className="border border-slate-200 rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700 border-b pb-2">
          <Image size={16} />
          Slide {activeSlideIndex + 1} Content
        </div>

        {/* Image Position */}
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium">Image Position:</label>
          <Select
            value={currentSlide.imagePosition || 'left'}
            onValueChange={(value) => updateSlide(activeSlideIndex, 'imagePosition', value)}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left">Left</SelectItem>
              <SelectItem value="right">Right</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Image Selection - Library Only */}
        <div className="flex gap-2 items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => openMediaPicker(blockId, activeSlideIndex)}
          >
            <FolderOpen size={16} className="mr-2" />
            {currentSlide.imageUrl ? 'Change Image' : 'Select Image'}
          </Button>
          {currentSlide.imageUrl && (
            <>
              <span className="text-xs text-green-600 flex items-center gap-1">
                ✓ Image selected
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateSlide(activeSlideIndex, 'imageUrl', '')}
                className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                title="Remove image"
              >
                <Trash2 size={14} />
              </Button>
            </>
          )}
        </div>

        {/* Alt Text */}
        <Input
          value={currentSlide.imageAlt || ''}
          onChange={(e) => updateSlide(activeSlideIndex, 'imageAlt', e.target.value)}
          placeholder="Alt text (for accessibility)"
        />

        {/* Image Size Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-slate-500 font-medium">Size:</label>
          <Select
            value={currentSlide.imageSizeMode || 'width'}
            onValueChange={(value) => updateSlide(activeSlideIndex, 'imageSizeMode', value)}
          >
            <SelectTrigger className="w-28 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="width">By Width</SelectItem>
              <SelectItem value="height">By Height</SelectItem>
              <SelectItem value="custom">Custom (W×H)</SelectItem>
            </SelectContent>
          </Select>
          
          {currentSlide.imageSizeMode === 'custom' ? (
            <>
              <span className="text-xs text-slate-500">W:</span>
              <Input
                type="number"
                value={currentSlide.imageWidth || 300}
                onChange={(e) => updateSlide(activeSlideIndex, 'imageWidth', parseInt(e.target.value) || 300)}
                className="w-16 h-8 text-xs"
                min="50"
                max="1200"
              />
              <span className="text-xs text-slate-500">H:</span>
              <Input
                type="number"
                value={currentSlide.imageHeight || 200}
                onChange={(e) => updateSlide(activeSlideIndex, 'imageHeight', parseInt(e.target.value) || 200)}
                className="w-16 h-8 text-xs"
                min="50"
                max="800"
              />
              <span className="text-xs text-slate-400">px</span>
            </>
          ) : currentSlide.imageSizeMode === 'height' ? (
            <>
              <span className="text-xs text-slate-500">H:</span>
              <Input
                type="number"
                value={currentSlide.imageHeight || 200}
                onChange={(e) => updateSlide(activeSlideIndex, 'imageHeight', parseInt(e.target.value) || 200)}
                className="w-16 h-8 text-xs"
                min="50"
                max="800"
              />
              <span className="text-xs text-slate-400">px</span>
            </>
          ) : (
            <>
              <span className="text-xs text-slate-500">W:</span>
              <Input
                type="number"
                value={currentSlide.imageWidth || 300}
                onChange={(e) => updateSlide(activeSlideIndex, 'imageWidth', parseInt(e.target.value) || 300)}
                className="w-16 h-8 text-xs"
                min="50"
                max="1200"
              />
              <span className="text-xs text-slate-400">px</span>
            </>
          )}
          
          <Select
            value={currentSlide.imageAlign || 'center'}
            onValueChange={(value) => updateSlide(activeSlideIndex, 'imageAlign', value)}
          >
            <SelectTrigger className="w-24 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left">Left</SelectItem>
              <SelectItem value="center">Center</SelectItem>
              <SelectItem value="right">Right</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Image Corners */}
        <div className="flex gap-2 items-center">
          <label className="text-sm font-medium">Corners:</label>
          <Select
            value={currentSlide.imageCorners || 'rounded'}
            onValueChange={(value) => updateSlide(activeSlideIndex, 'imageCorners', value)}
          >
            <SelectTrigger className="w-28 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="square">Square</SelectItem>
              <SelectItem value="rounded">Rounded</SelectItem>
              <SelectItem value="pill">Pill (max)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Shape Mask Controls */}
        <div className="flex gap-2 items-center flex-wrap">
          <label className="text-sm font-medium">Shape Mask:</label>
          <Select
            value={currentSlide.shapeMask || 'none'}
            onValueChange={(value) => updateSlide(activeSlideIndex, 'shapeMask', value)}
          >
            <SelectTrigger className="w-28 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {shapeMaskOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {currentSlide.shapeMask && currentSlide.shapeMask !== 'none' && (
            <>
              <label className="text-xs text-slate-500">Size:</label>
              <Input
                type="number"
                value={currentSlide.shapeSize || 250}
                onChange={(e) => updateSlide(activeSlideIndex, 'shapeSize', parseInt(e.target.value) || 250)}
                className="w-16 h-8 text-xs"
                min="50"
                max="600"
              />
              <span className="text-xs text-slate-400">px</span>
              
              <label className="text-xs text-slate-500 ml-2">Border:</label>
              <Input
                type="number"
                value={currentSlide.shapeBorderWidth || 0}
                onChange={(e) => updateSlide(activeSlideIndex, 'shapeBorderWidth', parseInt(e.target.value) || 0)}
                className="w-14 h-8 text-xs"
                min="0"
                max="20"
              />
              <input
                type="color"
                value={currentSlide.shapeBorderColor || '#ffffff'}
                onChange={(e) => updateSlide(activeSlideIndex, 'shapeBorderColor', e.target.value)}
                className="w-8 h-8 rounded border cursor-pointer"
                title="Border Color"
              />
            </>
          )}
        </div>

        {/* Padding Controls */}
        <div className="flex gap-2 items-center flex-wrap">
          <label className="text-xs text-slate-500">Padding:</label>
          <Select
            value={currentSlide.paddingSide || 'all'}
            onValueChange={(value) => updateSlide(activeSlideIndex, 'paddingSide', value)}
          >
            <SelectTrigger className="w-20 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="top">Top</SelectItem>
              <SelectItem value="right">Right</SelectItem>
              <SelectItem value="bottom">Bottom</SelectItem>
              <SelectItem value="left">Left</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="number"
            value={currentSlide.paddingSize || 0}
            onChange={(e) => updateSlide(activeSlideIndex, 'paddingSize', parseInt(e.target.value) || 0)}
            className="w-14 h-8 text-xs"
            min="0"
            max="100"
          />
          <span className="text-xs text-slate-400">px</span>
        </div>

        {/* Text Content */}
        <div className="space-y-2">
          <label className="text-xs text-slate-500 block">Text Content</label>
          
          {/* TipTap Rich Text Editor */}
          {/* key={activeSlideIndex} forces TipTap to remount when the user
              switches slides, so each slide sees its own textHtml. Without
              this, all slides share whatever was first loaded. */}
          <TipTapEditor
            key={`slide-${activeSlideIndex}`}
            content={currentSlide.textHtml || '<p>Text content</p>'}
            onChange={(html) => updateSlide(activeSlideIndex, 'textHtml', html)}
            placeholder="Enter text content..."
          />
          
          {/* Add Button Option for this slide */}
          <div className="mt-3 pt-3 border-t border-slate-200">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={currentSlide.hasButton || false}
                onChange={(e) => updateSlide(activeSlideIndex, 'hasButton', e.target.checked)}
                className="w-4 h-4 rounded border-slate-300"
              />
              <span className="text-xs font-medium text-slate-600">Add Button</span>
            </label>
            
            {currentSlide.hasButton && (
              <div className="mt-2 space-y-2 p-2 bg-slate-50 rounded-lg">
                <Input
                  value={currentSlide.buttonText || 'Learn More'}
                  onChange={(e) => updateSlide(activeSlideIndex, 'buttonText', e.target.value)}
                  placeholder="Button Text"
                  className="h-8 text-sm"
                />
                <LinkSelector
                  value={currentSlide.buttonUrl || ''}
                  onChange={(url) => updateSlide(activeSlideIndex, 'buttonUrl', url)}
                  pages={pages}
                  anchors={anchors}
                  label="Button URL"
                  testId={`carousel-slide-${activeSlideIndex}-button-url`}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    value={currentSlide.buttonStyle || 'solid'}
                    onValueChange={(value) => updateSlide(activeSlideIndex, 'buttonStyle', value)}
                  >
                    <SelectTrigger className="w-20 h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="solid">Solid</SelectItem>
                      <SelectItem value="outline">Outline</SelectItem>
                      <SelectItem value="ghost">Ghost</SelectItem>
                      <SelectItem value="gradient">Gradient</SelectItem>
                      <SelectItem value="soft">Soft</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select
                    value={currentSlide.buttonSize || 'md'}
                    onValueChange={(value) => updateSlide(activeSlideIndex, 'buttonSize', value)}
                  >
                    <SelectTrigger className="w-16 h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sm">Sm</SelectItem>
                      <SelectItem value="md">Md</SelectItem>
                      <SelectItem value="lg">Lg</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-slate-500">BG:</label>
                    <input
                      type="color"
                      value={currentSlide.buttonColor || '#6366f1'}
                      onChange={(e) => updateSlide(activeSlideIndex, 'buttonColor', e.target.value)}
                      className="w-6 h-6 rounded border cursor-pointer"
                    />
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-slate-500">Text:</label>
                    <input
                      type="color"
                      value={currentSlide.buttonTextColor || '#ffffff'}
                      onChange={(e) => updateSlide(activeSlideIndex, 'buttonTextColor', e.target.value)}
                      className="w-6 h-6 rounded border cursor-pointer"
                    />
                  </div>

                  {currentSlide.buttonStyle === 'gradient' && (
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-slate-500">Grad:</label>
                      <input
                        type="color"
                        value={currentSlide.buttonGradientColor || '#8b5cf6'}
                        onChange={(e) => updateSlide(activeSlideIndex, 'buttonGradientColor', e.target.value)}
                        className="w-6 h-6 rounded border cursor-pointer"
                      />
                    </div>
                  )}
                </div>

                <ButtonEnhancements
                  fields={currentSlide}
                  testPrefix={`carousel-slide-${activeSlideIndex}-btn`}
                  onPatch={(patch) => Object.entries(patch).forEach(([k, v]) => updateSlide(activeSlideIndex, k, v))}
                />
                
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    value={currentSlide.buttonAlign || 'left'}
                    onValueChange={(value) => updateSlide(activeSlideIndex, 'buttonAlign', value)}
                  >
                    <SelectTrigger className="w-20 h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">Left</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="right">Right</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <label className="flex items-center gap-1 text-xs text-slate-500">
                    <input
                      type="checkbox"
                      checked={currentSlide.buttonFullWidth || false}
                      onChange={(e) => updateSlide(activeSlideIndex, 'buttonFullWidth', e.target.checked)}
                      className="w-3 h-3 rounded"
                    />
                    Full Width
                  </label>
                </div>
                
                {/* Button Preview */}
                <div className="mt-2 p-2 bg-white rounded text-center">
                  <div style={{ textAlign: currentSlide.buttonAlign || 'left' }}>
                    <button
                      className={`
                        ${currentSlide.buttonSize === 'sm' ? 'px-3 py-1 text-sm' : ''}
                        ${currentSlide.buttonSize === 'lg' ? 'px-6 py-3 text-lg' : ''}
                        ${currentSlide.buttonSize === 'md' || !currentSlide.buttonSize ? 'px-4 py-2' : ''}
                        ${currentSlide.buttonFullWidth ? 'w-full' : ''}
                        ${currentSlide.buttonStyle === 'outline' ? 'bg-transparent border-2' : ''}
                        ${currentSlide.buttonStyle === 'ghost' ? 'bg-transparent' : ''}
                        rounded-lg font-medium transition-all
                      `}
                      style={{
                        backgroundColor: currentSlide.buttonStyle === 'solid' || !currentSlide.buttonStyle ? (currentSlide.buttonColor || '#6366f1') : 'transparent',
                        color: currentSlide.buttonStyle === 'solid' || !currentSlide.buttonStyle ? (currentSlide.buttonTextColor || '#ffffff') : (currentSlide.buttonColor || '#6366f1'),
                        borderColor: currentSlide.buttonStyle === 'outline' ? (currentSlide.buttonColor || '#6366f1') : 'transparent',
                      }}
                    >
                      {currentSlide.buttonText || 'Learn More'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Image Preview */}
        {currentSlide.imageUrl && (
          <div className="mt-2 p-2 bg-slate-50 rounded" style={{ textAlign: currentSlide.imageAlign || 'center' }}>
            <p className="text-xs text-slate-400 mb-2">Preview:</p>
            <img 
              src={currentSlide.imageUrl} 
              alt={currentSlide.imageAlt || ''} 
              className="rounded inline-block max-h-40 object-cover"
              style={{
                maxWidth: '100%',
                width: currentSlide.shapeMask && currentSlide.shapeMask !== 'none' 
                  ? `${currentSlide.shapeSize || 250}px` 
                  : `${currentSlide.imageWidth || 300}px`,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageTextCarouselEditor;
